import { apiClient } from '@/lib/api/client'

export type TicketOption = {
  code: string
  label: string
}

type TicketOptionApiRow = {
  code?: string
  label?: string | null
  active?: boolean
}

function normalizeRows(payload: unknown): TicketOptionApiRow[] {
  if (Array.isArray(payload)) return payload as TicketOptionApiRow[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: TicketOptionApiRow[] }).data
  }
  return []
}

/**
 * Fetch active option values for a ticket option list group.
 * Backed by ticket_option_groups / ticket_option_values on the ticketing DB.
 */
export async function getOptionsByGroup(groupCode: string): Promise<TicketOption[]> {
  const { data } = await apiClient.get<unknown>(`/options/ticket-option-lists/${groupCode}`)
  return normalizeRows(data)
    .filter((row) => row?.code)
    .filter((row) => row.active !== false)
    .map((row) => ({
      code: String(row.code ?? ''),
      label: String(row.label?.trim() || row.code || ''),
    }))
}

/** Build a code → label map for display helpers (e.g. severity badges). */
export function optionsToLabelMap(options: TicketOption[]): Record<string, string> {
  return Object.fromEntries(options.map((o) => [o.code, o.label]))
}

/** Codes only — for RadioRow components that use code as both value and display. */
export function optionCodes(options: TicketOption[]): string[] {
  return options.map((o) => o.code)
}

/** Keep the current form value visible even if it is not in the fetched list yet. */
export function mergeTicketSelectOptions(
  options: TicketOption[],
  currentValue: string | undefined,
  labelMap: Record<string, string>,
): TicketOption[] {
  const current = currentValue?.trim()
  if (!current || options.some((o) => o.code === current)) {
    return options
  }
  return [{ code: current, label: labelMap[current] ?? current }, ...options]
}
